import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaUserCircle } from 'react-icons/fa';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import {
  Button,
  Grid,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useBanner,
} from '@tq/ui-components';
import { tqc } from '@tq/ui-components/dist/system';

import { useDeleteConsultee } from 'api';
import { MobileModal } from 'components';
import { BANNER_TIMEOUT } from 'config';
import { useIsMobile } from '@tq/ui-components/dist/hooks';
import { ApplicationUser } from 'types';

type ManageTeamModalProps = {
  removeUserData: ApplicationUser | undefined;
  onClose: () => void;
};

export const RemoveUserModal: React.FC<ManageTeamModalProps> = ({
  removeUserData,
  onClose,
}) => {
  const { t } = useTranslation('manage_team_modal');
  const isMobile = useIsMobile();
  const banner = useBanner();
  const { mutate: deleteConsultee } = useDeleteConsultee();

  const handleError = () => {
    onClose();
    banner({
      id: 'remove-user-banner-fail',
      title: t('banner_user_removed_failed', 'User cannot be removed. Please try again.'),
      duration: BANNER_TIMEOUT,
      status: 'error',
    });
  };

  const handleRemove = () => {
    if (removeUserData?.id) {
      deleteConsultee(removeUserData.id, {
        onSuccess: () => {
          onClose();
          banner({
            id: 'remove-user-banner-success',
            title: t('banner_user_removed', 'User removed'),
            duration: BANNER_TIMEOUT,
            status: 'success',
          });
        },
        onError: () => {
          handleError();
        },
      });
    } else {
      handleError();
    }
  };

  const content = (
    <>
      <ModalHeader>
        <Grid gridAutoFlow='column' gridGap={4}>
          <FaUserCircle color={COLOURS.blue[600]} />
          <span>{t('remove_user_heading', 'Remove user')}</span>
        </Grid>
        <ModalCloseButton />
      </ModalHeader>
      <ModalBody aria-label='Remove user modal body' overflow='visible'>
        <tqc.p mb='1rem'>
          {t('remove_user_body', 'You are about to remove the following user:')}
        </tqc.p>
        <span>{removeUserData?.name + ' <' + removeUserData?.email + '>'}</span>
      </ModalBody>
    </>
  );

  return isMobile ? (
    <MobileModal aria-label='Remove user modal' isOpen onClose={onClose}>
      {content}
      <ModalFooter>
        <Grid gridGap={4} w='100%'>
          <Button variant='danger' isFullWidth type='submit' onClick={handleRemove}>
            {t('remove_user_text', 'Remove')}
          </Button>
          <Button isFullWidth variant='tertiary' onClick={onClose}>
            {t('cancel_text', 'Cancel')}
          </Button>
        </Grid>
      </ModalFooter>
    </MobileModal>
  ) : (
    <Modal isOpen onClose={onClose}>
      <ModalOverlay />
      <ModalContent aria-label='Remove user modal'>
        {content}
        <ModalFooter justifyContent='end'>
          <Grid gridAutoFlow='column' gridGap={4}>
            <Button variant='tertiary' onClick={onClose}>
              {t('cancel_text', 'Cancel')}
            </Button>
            <Button variant='danger' type='submit' onClick={handleRemove}>
              {t('remove_user_text', 'Remove')}
            </Button>
          </Grid>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
